import React, { useState, useEffect } from 'react';
import { getFulfillments, dispatchFulfillment, deliverFulfillment } from '../../api/fulfillments';
import Header from '../../Components/Header/Header';
import Sidebar from '../../Components/Sidebar/Sidebar';
import ConfirmationModal from '../../Components/Shared/ConfirmationModal';
import CustomDropdown from '../../Components/Shared/CustomDropdown';
import '../AdventureShared.css';
import './FulfillmentDashboardPage.css';

const CARRIER_OPTIONS = [
  { value: 'Canada Post', label: 'Canada Post' },
  { value: 'UPS', label: 'UPS' },
  { value: 'FedEx', label: 'FedEx' },
  { value: 'DHL', label: 'DHL' },
  { value: 'Other', label: 'Other' }
];

export default function FulfillmentDashboardPage() {
  const [fulfillments, setFulfillments] = useState([]);
  const [activeTab, setActiveTab] = useState('all'); // all | pending | dispatched | delivered
  const [isLoading, setIsLoading] = useState(true);

  // Dispatch Modal state
  const [dispatchItem, setDispatchItem] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('UPS');
  const [notes, setNotes] = useState('');
  const [isSubmittingDispatch, setIsSubmittingDispatch] = useState(false);

  // Delivery confirmation state
  const [deliverItem, setDeliverItem] = useState(null);
  const [isSubmittingDeliver, setIsSubmittingDeliver] = useState(false);

  useEffect(() => {
    fetchFulfillments();
  }, [activeTab]);

  const fetchFulfillments = async () => {
    setIsLoading(true);
    try {
      const statusParam = activeTab === 'all' ? null : activeTab;
      const data = await getFulfillments({ status: statusParam });
      setFulfillments(data || []);
    } catch (err) {
      console.error("Failed to load fulfillments", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDispatch = (item) => {
    setDispatchItem(item);
    setTrackingNumber('');
    setCarrier('UPS');
    setNotes('');
  };

  const handleDispatchSubmit = async (e) => {
    e.preventDefault();
    if (!dispatchItem) return;

    setIsSubmittingDispatch(true);
    try {
      await dispatchFulfillment(dispatchItem.fulfillmentId || dispatchItem.id, {
        trackingNumber: trackingNumber.trim() || null,
        carrier,
        notes: notes.trim() || null
      });
      setDispatchItem(null);
      fetchFulfillments();
    } catch (err) {
      console.error("Failed to dispatch fulfillment", err);
      alert("Failed to mark dispatch. Please try again.");
    } finally {
      setIsSubmittingDispatch(false);
    }
  };

  const handleDeliverConfirm = async () => {
    if (!deliverItem) return;

    setIsSubmittingDeliver(true);
    try {
      await deliverFulfillment(deliverItem.fulfillmentId || deliverItem.id);
      setDeliverItem(null);
      fetchFulfillments();
    } catch (err) {
      console.error("Failed to deliver fulfillment", err);
      alert("Failed to mark delivery. Please try again.");
    } finally {
      setIsSubmittingDeliver(false);
    }
  };

  const getMethodLabel = (method) => {
    switch (method) {
      case 'pre_ship': return 'Pre-ship';
      case 'at_event': return 'At Event';
      case 'digital': return 'Digital';
      default: return method;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="ful-dash-page">
      <div className="adventurePageContainer">
        <div className="headerContain">
          <Header />
        </div>

        <div className="adventurePageContainer2">
          <div className="sidebarContain">
            <Sidebar />
          </div>

          <main className="adv-page-main animate-fade-in">
            <div className="ful-dash-container">
              <div className="adv-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
                <div>
                  <h1 className="adv-page-title">
                    Fulfillment <span className="adv-page-title-accent">Dashboard</span>
                  </h1>
                  <p className="adv-page-sub">
                    Manage and track order fulfillments, dispatches, and delivery statuses.
                  </p>
                </div>

                <div className="ful-tabs">
                  <button
                    className={`ful-tab-btn ${activeTab === 'all' ? 'active' : ''}`}
                    onClick={() => setActiveTab('all')}
                  >
                    All
                  </button>
                  <button
                    className={`ful-tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pending')}
                  >
                    Pending
                  </button>
                  <button
                    className={`ful-tab-btn ${activeTab === 'dispatched' ? 'active' : ''}`}
                    onClick={() => setActiveTab('dispatched')}
                  >
                    Dispatched
                  </button>
                  <button
                    className={`ful-tab-btn ${activeTab === 'delivered' ? 'active' : ''}`}
                    onClick={() => setActiveTab('delivered')}
                  >
                    Delivered
                  </button>
                </div>
              </div>

              {isLoading ? (
                <div style={{ textAlign: 'center', padding: '100px 0' }}>
                  <div className="off-create-spinner" style={{ margin: '0 auto 20px' }}></div>
                  <p style={{ color: 'rgba(255,255,255,0.6)' }}>Loading orders...</p>
                </div>
              ) : fulfillments.length === 0 ? (
                <div className="ful-empty-state">
                  <div className="ful-empty-icon">📦</div>
                  <p className="ful-empty-text">No fulfillment orders found in this section.</p>
                </div>
              ) : (
                <div className="ful-grid">
                  {fulfillments.map((item) => (
                    <div key={item.fulfillmentId || item.id} className="ful-card" style={item.status === 'cancelled' ? { opacity: 0.55, borderStyle: 'dashed' } : {}}>
                      <div className="ful-card-header">
                        <div>
                          <h3 className="ful-card__customer">{item.customerName}</h3>
                        </div>
                        <span className={`adv-status adv-status--${item.status}`}>
                          {item.status}
                        </span>
                      </div>

                      <div className="ful-card-body">
                        <div className="ful-card__img-box">
                          {item.product?.imageUrl ? (
                            <img src={item.product.imageUrl} alt={item.product.name} className="ful-card__img" />
                          ) : (
                            <span className="ful-card__placeholder">📦</span>
                          )}
                        </div>

                        <div className="ful-card-info">
                          <h4 className="ful-card__prod-name">{item.product?.name}</h4>
                          <p className="ful-card__qty">Quantity: {item.quantity}</p>
                          <p className="ful-card__date">Scheduled: {formatDate(item.bookingDate)}</p>
                        </div>
                      </div>

                      <div className="ful-badge-row" style={{ flexWrap: 'wrap', gap: '6px' }}>
                        <span className="prod-badge prod-badge--method">
                          {getMethodLabel(item.product?.deliveryMethod)}
                        </span>
                        {item.product?.sku && (
                          <span className="prod-badge prod-badge--price" style={{ fontFamily: 'monospace' }}>
                            SKU: {item.product.sku}
                          </span>
                        )}
                        {item.status !== 'cancelled' && item.product?.quantity !== undefined && item.product?.quantity <= 0 && (
                          <span className="prod-badge" style={{ backgroundColor: 'rgba(220, 53, 69, 0.2)', color: '#ff4d5e', border: '1px solid rgba(220, 53, 69, 0.4)' }}>
                            🚫 Out of Stock
                          </span>
                        )}
                        {item.status !== 'cancelled' && item.product?.quantity !== undefined && item.product?.quantity > 0 && item.product?.quantity < 3 && (
                          <span className="prod-badge" style={{ backgroundColor: 'rgba(219, 137, 28, 0.2)', color: '#ffb338', border: '1px solid rgba(219, 137, 28, 0.4)' }}>
                            ⚠️ Low Stock ({item.product.quantity} left)
                          </span>
                        )}
                      </div>

                      {item.product?.deliveryMethod === 'pre_ship' && (
                        <div className="ful-card__shipping-address" style={{ marginTop: '12px', padding: '10px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px', fontSize: '13px' }}>
                          <strong style={{ color: '#48d1cc', display: 'block', marginBottom: '4px' }}>Shipping Address:</strong>
                          <span style={{ color: '#ccc', lineHeight: '1.4', display: 'block' }}>
                            {item.shippingAddressLine || 'No Address Provided'}<br />
                            {item.shippingCity || ''}, {item.shippingState || ''} {item.shippingZipcode || ''}<br />
                            {item.shippingCountry || ''}
                          </span>
                        </div>
                      )}

                      {(item.status === 'dispatched' || item.status === 'delivered') && item.trackingNumber && (
                        <div className="ful-card__tracking-details">
                          <span>Carrier: {item.carrier}</span>
                          <span>Tracking: {item.trackingNumber}</span>
                        </div>
                      )}

                      <div className="ful-card-footer">
                        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>
                          Placed {formatDate(item.createdAt)}
                        </span>

                        {item.status === 'pending' && (
                          <button
                            className="ful-card__btn"
                            onClick={() => handleOpenDispatch(item)}
                          >
                            Mark as Dispatched
                          </button>
                        )}

                        {item.status === 'dispatched' && (
                          <button
                            className="ful-card__btn"
                            style={{ backgroundColor: '#28a745', color: '#ffffff' }}
                            onClick={() => setDeliverItem(item)}
                          >
                            Mark as Delivered
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </main>
        </div>
      </div>

      {/* Page 6.4 — Mark Dispatched Modal */}
      {dispatchItem && (
        <div className="ful-modal-overlay">
          <div className="ful-modal animate-fade-in">
            <h2 className="ful-modal-title">Mark Order as Dispatched</h2>
            <p style={{ margin: 0, fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
              Provide dispatch information for <strong>{dispatchItem.product?.name}</strong> to notify {dispatchItem.customerName}.
            </p>

            <form className="ful-modal-form" onSubmit={handleDispatchSubmit}>
              <div className="ful-modal-group">
                <label className="ful-modal-label">Carrier</label>
                <CustomDropdown
                  value={carrier}
                  onChange={setCarrier}
                  options={CARRIER_OPTIONS}
                />
              </div>

              <div className="ful-modal-group">
                <label className="ful-modal-label">Tracking Number (Optional)</label>
                <input
                  type="text"
                  placeholder="e.g. 1Z999AA10123456784"
                  className="ful-modal-input"
                  value={trackingNumber}
                  onChange={e => setTrackingNumber(e.target.value)}
                />
              </div>

              <div className="ful-modal-group">
                <label className="ful-modal-label">Fulfillment Notes (Optional)</label>
                <textarea
                  placeholder="e.g. Left in secondary mail pouch, fragile wrap..."
                  className="ful-modal-textarea"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                />
              </div>

              <div className="ful-modal-actions">
                <button
                  type="button"
                  className="ful-modal-btn-cancel"
                  onClick={() => setDispatchItem(null)}
                  disabled={isSubmittingDispatch}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="ful-modal-btn-confirm"
                  disabled={isSubmittingDispatch}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                >
                  {isSubmittingDispatch ? (
                    <>
                      <span className="adv-btn-spinner" style={{ borderLeftColor: '#071414' }}></span>
                      Confirming...
                    </>
                  ) : (
                    'Confirm Dispatch'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mark Delivered Confirmation */}
      <ConfirmationModal
        isOpen={!!deliverItem}
        onClose={() => setDeliverItem(null)}
        onConfirm={handleDeliverConfirm}
        title="Confirm Delivery?"
        message={`Are you sure you want to mark ${deliverItem?.product?.name} as successfully delivered to ${deliverItem?.customerName}?`}
        confirmText="Yes, Delivered"
        cancelText="Cancel"
        isLoading={isSubmittingDeliver}
      />
    </div>
  );
}
